import React from "react";
import { useTranslation } from "react-i18next";
import { Modal, Button, Steps, Spin, Result } from "antd";

import { useAddSceneToTherapyWizard } from "./useAddSceneToTherapyWizard";
import {
  Step1SelectTherapy,
  Step2SceneDescription,
  Step3SelectEvaluations,
  Step4Periodicity,
} from "./Steps";

import "./AddSceneToTherapyWizard.scss";
import { useMovil } from "../../../../hooks/useIsNarrow";
import { useHistorialHoja } from "../../../../hooks/useHistorialHoja";

export function AddSceneToTherapyWizard({ isOpen, onClose, scene, onSuccess }) {
  const movil = useMovil();

  // El boton atras del telefono cierra el asistente en vez de sacar de la pagina.
  useHistorialHoja(movil && isOpen, onClose);

  // "Cancelar" y "Siguiente" ya viven en el bloque comun.
  const { t } = useTranslation(["rooms", "common"]);

  const {
    currentStep,
    next,
    prev,
    canProceed,
    submitting,

    // step data
    therapiesLoading,
    therapies,
    selectedTherapyId,
    setSelectedTherapyId,
    therapySearch,
    setTherapySearch,

    description,
    setDescription,

    evalTab,
    setEvalTab,
    evalSearch,
    setEvalSearch,
    evalLoading,
    standardEvaluations,
    myEvaluations,
    selectedEvalIds,
    toggleEvaluation,

    previewOpen,
    previewLoading,
    previewEvaluation,
    openPreview,
    closePreview,

    periodicity,
    setPeriodicity,

    handleSubmit,
    therapistId,
  } = useAddSceneToTherapyWizard({ isOpen, scene, onSuccess, onClose });

  const stepsItems = [
    { key: "therapy", title: "" },
    { key: "desc", title: "" },
    { key: "evals", title: "" },
    { key: "periodicity", title: "" },
  ];

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <Step1SelectTherapy
            loading={therapiesLoading}
            therapies={therapies}
            selectedTherapyId={selectedTherapyId}
            onSelect={setSelectedTherapyId}
            search={therapySearch}
            onSearch={setTherapySearch}
          />
        );
      case 1:
        return (
          <Step2SceneDescription
            scene={scene}
            description={description}
            onChange={setDescription}
          />
        );
      case 2:
        return (
          <Step3SelectEvaluations
            tab={evalTab}
            onTabChange={setEvalTab}
            search={evalSearch}
            onSearch={setEvalSearch}
            loading={evalLoading}
            standardEvaluations={standardEvaluations}
            myEvaluations={myEvaluations}
            selectedEvalIds={selectedEvalIds}
            onToggle={toggleEvaluation}
            onPreview={openPreview}
            previewOpen={previewOpen}
            previewLoading={previewLoading}
            previewEvaluation={previewEvaluation}
            onClosePreview={closePreview}
          />
        );
      case 3:
        return (
          <Step4Periodicity
            periodicity={periodicity}
            onChange={setPeriodicity}
            selectedCount={selectedEvalIds.length}
          />
        );
      default:
        return null;
    }
  };

  const totalSteps = 4;

  const showFatal = !scene || (isOpen && therapistId === null); // sin therapistId no podremos listar terapias propias

  return (
    <Modal
      open={isOpen}
      visible={isOpen}
      onCancel={onClose}
      footer={null}
      width={900}
      centered
      destroyOnHidden={true}
      className="revi-addscene-wizard-modal"
      rootClassName="revi-hoja"
    >
      <div className="wizard-header">
        <div className="step-indicator">
          <span className="step-badge">
            {t("common:asistente.pasoDe", {
              actual: currentStep + 1,
              total: totalSteps,
            })}
          </span>
          <h3>{t("asistenteEscena.titulo")}</h3>
        </div>

        <div className="mini-steps">
          <Steps current={currentStep} size="small" items={stepsItems} />
        </div>
      </div>

      <div className="wizard-content">
        {showFatal ? (
          <Result
            status="warning"
            title={t("asistenteEscena.errorTitulo")}
            subTitle={
              !scene
                ? t("asistenteEscena.errorSinEscena")
                : t("asistenteEscena.errorSinTerapeuta")
            }
          />
        ) : (
          renderStep()
        )}
      </div>

      <div className="wizard-actions">
        <Button onClick={onClose}>{t("common:actions.cancel")}</Button>

        <div style={{ display: "flex", gap: 8 }}>
          {currentStep > 0 ? (
            <Button onClick={prev} disabled={submitting}>
              {t("acciones.atras")}
            </Button>
          ) : null}

          <Button
            type="primary"
            onClick={currentStep === totalSteps - 1 ? handleSubmit : next}
            disabled={!canProceed() || submitting || showFatal}
            loading={submitting}
          >
            {currentStep === totalSteps - 1
              ? t("asistenteEscena.guardarYFinalizar")
              : t("common:actions.next")}
          </Button>
        </div>
      </div>

      {(therapiesLoading || evalLoading) && currentStep !== 2 ? (
        <div className="wizard-global-loading">
          <Spin />
        </div>
      ) : null}
    </Modal>
  );
}
